/**
 * Typed API errors.
 *
 * The API returns RFC 9457 problem details:
 *   { type, title, status, detail, code, errors: [...] }
 * Every failed call made through `unwrap()` (see client.ts) throws an ApiError,
 * including network failures (status 0, code "network_error").
 */

export interface FieldError {
  /** Dotted field path, for example "settings.retention_days". Empty for form-level errors. */
  path: string
  message: string
}

export interface ProblemDetails {
  type?: string
  title?: string
  status?: number
  detail?: string
  code?: string
  errors?: unknown[]
  [key: string]: unknown
}

export class ApiError extends Error {
  readonly status: number
  readonly title: string
  readonly detail: string | undefined
  readonly code: string
  readonly type: string | undefined
  readonly errors: FieldError[]
  readonly requestId: string | undefined
  readonly raw: unknown

  constructor(init: {
    status: number
    title: string
    detail?: string
    code: string
    type?: string
    errors?: FieldError[]
    requestId?: string
    raw?: unknown
  }) {
    super(init.detail ? `${init.title}: ${init.detail}` : init.title)
    this.name = 'ApiError'
    this.status = init.status
    this.title = init.title
    this.detail = init.detail
    this.code = init.code
    this.type = init.type
    this.errors = init.errors ?? []
    this.requestId = init.requestId
    this.raw = init.raw
  }

  get isNetworkError(): boolean {
    return this.status === 0
  }
  get isUnauthorized(): boolean {
    return this.status === 401
  }
  get isForbidden(): boolean {
    return this.status === 403
  }
  get isNotFound(): boolean {
    return this.status === 404
  }
  get isValidation(): boolean {
    return this.status === 400 || this.status === 422
  }
  get isConflict(): boolean {
    return this.status === 409
  }

  /** Short, user-facing sentence. */
  get userMessage(): string {
    return this.detail ?? this.title
  }
}

const STATUS_TITLES: Record<number, string> = {
  400: 'Invalid request',
  401: 'Sign-in required',
  403: 'Not allowed',
  404: 'Not found',
  409: 'Conflict',
  422: 'Validation failed',
  429: 'Too many requests',
  500: 'Server error',
  502: 'API unavailable',
  503: 'API unavailable',
  504: 'API timed out',
}

const STATUS_CODES: Record<number, string> = {
  400: 'invalid_request',
  401: 'unauthorized',
  403: 'forbidden',
  404: 'not_found',
  409: 'conflict',
  422: 'invalid_request',
  429: 'rate_limited',
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() !== '' ? v : undefined
}

/** "/settings/retention_days" or "#/a/0" (JSON Pointer) -> "settings.retention_days" / "a.0" */
export function normalizeFieldPath(p: string): string {
  let s = p.trim()
  if (s.startsWith('#')) s = s.slice(1)
  if (s.startsWith('/')) {
    s = s
      .slice(1)
      .split('/')
      .map((seg) => seg.replace(/~1/g, '/').replace(/~0/g, '~'))
      .join('.')
  }
  // body.foo / $.foo prefixes some validators add
  s = s.replace(/^\$\.?/, '').replace(/^body\./, '')
  // a[0].b -> a.0.b
  s = s.replace(/\[(\d+)\]/g, '.$1')
  return s
}

/** Accepts the common shapes for `errors[]` entries and normalizes them. */
export function parseFieldErrors(errors: unknown): FieldError[] {
  if (!Array.isArray(errors)) {
    // Also accept { field: "message" } or { field: ["message"] } maps.
    if (isRecord(errors)) {
      return Object.entries(errors).flatMap(([path, v]) =>
        (Array.isArray(v) ? v : [v])
          .map((m) => str(m))
          .filter((m): m is string => !!m)
          .map((message) => ({ path: normalizeFieldPath(path), message })),
      )
    }
    return []
  }
  const out: FieldError[] = []
  for (const e of errors) {
    if (typeof e === 'string') {
      out.push({ path: '', message: e })
      continue
    }
    if (!isRecord(e)) continue
    const path =
      str(e.field) ?? str(e.path) ?? str(e.pointer) ?? str(e.location) ?? str(e.name) ?? ''
    const message = str(e.message) ?? str(e.detail) ?? str(e.title) ?? str(e.reason)
    if (message) out.push({ path: normalizeFieldPath(path), message })
  }
  return out
}

/** Build an ApiError from an HTTP status and a parsed (or unparsed) body. */
export function toApiError(status: number, body: unknown, requestId?: string): ApiError {
  const p: ProblemDetails = isRecord(body) ? body : {}
  const fallbackTitle =
    STATUS_TITLES[status] ?? (status >= 500 ? 'Server error' : `Request failed (${status})`)
  const title = str(p.title) ?? fallbackTitle
  let detail = str(p.detail) ?? str(p.message)
  if (!detail && typeof body === 'string' && body.trim() && body.length < 300) detail = body.trim()
  return new ApiError({
    status,
    title,
    detail,
    code: str(p.code) ?? STATUS_CODES[status] ?? (status >= 500 ? 'server_error' : 'error'),
    type: str(p.type),
    errors: parseFieldErrors(p.errors),
    requestId: str(p.request_id) ?? requestId,
    raw: body,
  })
}

export function networkError(cause: unknown): ApiError {
  return new ApiError({
    status: 0,
    title: 'Can’t reach the MailSmith API',
    detail: 'Check that the API is running and that this site is in its CORS_ORIGINS list.',
    code: 'network_error',
    raw: cause,
  })
}

/** Normalize anything thrown into an ApiError (non-API errors become status -1). */
export function asApiError(err: unknown): ApiError {
  if (err instanceof ApiError) return err
  if (err instanceof TypeError) return networkError(err)
  return new ApiError({
    status: -1,
    title: 'Something went wrong',
    detail: err instanceof Error ? err.message : String(err),
    code: 'client_error',
    raw: err,
  })
}
